import { useDispatch, useSelector } from "react-redux";
import { useLocation, NavLink } from "react-router-dom";
import { Disclosure, Transition } from "@headlessui/react";
import { TiArrowSortedDown } from "react-icons/ti";
import { GoChecklist, GoTasklist } from "react-icons/go";
import { AiOutlineProduct } from "react-icons/ai";
import {
  FiMessageSquare,
  FiUser,
} from "react-icons/fi";
import { HiOutlineInbox, HiOutlineBuildingOffice2, HiOutlineUserGroup, HiOutlineEnvelope } from "react-icons/hi2";
import styles from "./index.module.css";
import { useEffect, useRef, useState } from "react";
import ApiClient from "../../../methods/api/apiClient";
import { sidebar_base } from "../../../actions/sidebar";
import {
  MdOutlineDisplaySettings,
  MdOutlineHomeWork,
  MdOutlinePayment,
  MdOutlineLocalOffer,
  MdOutlineSettings,
} from "react-icons/md";
import { LiaFileInvoiceDollarSolid } from "react-icons/lia";
import { LuListTodo } from "react-icons/lu";
import { PiUserList } from "react-icons/pi";
import { RxDashboard } from "react-icons/rx";
import { TbBriefcase } from "react-icons/tb";

const Html = ({ tabclass, isOpen, setCollapsedState, urlAllow }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const navContainerRef = useRef(null);

  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread inquiries badge count dynamically from API
  useEffect(() => {
    let isMounted = true;
    const fetchSidebarBadges = async () => {
      try {
        const res = await ApiClient.get("contactUs/listing?page=1&limit=1&status=all");
        if (res?.success && isMounted) {
          const count = res?.unreadCount ?? (res?.data?.unreadCount ?? 0);
          setUnreadCount(Number(count) || 0);
        }
      } catch (e) {
        // quiet error
      }
    };
    fetchSidebarBadges();
    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  const CommonTransitionClasses = {
    enter: "transition duration-200 ease-in-out",
    enterFrom: "transform scale-95 opacity-0",
    enterTo: "transform scale-100 opacity-100",
    leave: "transition duration-150 ease-in-out",
    leaveFrom: "transform scale-100 opacity-100",
    leaveTo: "transform scale-95 opacity-0",
  };

  const CommonArrow = (open) => {
    return (
      <TiArrowSortedDown
        className={`${
          open ? "" : "-rotate-90 transform"
        } transition-transform duration-200 h-4 w-4 text-slate-500`}
      />
    );
  };

  // Permission check helper for staff / sub-admins
  const checkAllow = (urlKey) => {
    if (!urlAllow) return true;
    return urlAllow(urlKey);
  };

  // Reusable NavLink Builder
  const renderNavLink = (to, label, icon, badge = null, exact = false) => (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `group relative px-3 py-2.5 rounded-xl flex items-center justify-between text-xs sm:text-sm transition-all duration-150 !no-underline ${
          isActive
            ? "bg-[#115B9D] text-white shadow-sm shadow-[#115B9D]/30 !font-bold"
            : "text-slate-700 hover:text-slate-900 hover:bg-slate-100 !font-semibold"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                isActive
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-700 group-hover:bg-[#115B9D]/10 group-hover:text-[#115B9D]"
              }`}
            >
              {icon}
            </div>
            <span className="sidebar_text leading-none font-bold truncate">
              {label}
            </span>
          </div>
          {badge ? <div className="shrink-0">{badge}</div> : null}
        </>
      )}
    </NavLink>
  );

  const getSidebarItems = () => {
    const list = [];

    // 1. Dashboard
    list.push({
      key: "dashboard",
      element: (
        <li key="nav-dashboard">
          {renderNavLink("/dashboard", "Dashboard", <RxDashboard className="w-4 h-4" />, null, true)}
        </li>
      ),
    });

    // 2. Tenants (Multi-Tenant Management)
    if (checkAllow("tenants,tenant")) {
      list.push({
        key: "tenants",
        element: (
          <li key="nav-tenants">
            {renderNavLink(
              "/tenants",
              "Tenants",
              <HiOutlineBuildingOffice2 className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }

    // 3. Contact Us
    if (checkAllow("contactUs,contact-us")) {
      list.push({
        key: "contact-us",
        element: (
          <li key="nav-contact-us">
            {renderNavLink(
              "/contact-us",
              "Contact Us",
              <HiOutlineInbox className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }

    // 3. Subscription Plans
    if (checkAllow("plans")) {
      list.push({
        key: "plans",
        element: (
          <li key="nav-plans">
            {renderNavLink(
              "/plans",
              "Plans",
              <LiaFileInvoiceDollarSolid className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }

    // 4. Transactions (Super Admin)
    if (checkAllow("transactions")) {
      list.push({
        key: "transactions",
        element: (
          <li key="nav-transactions">
            {renderNavLink(
              "/transactions",
              "Transactions",
              <MdOutlinePayment className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }

    // 5. Staff & Sub-Admins
    if (checkAllow("staff,sub-admin,subadmin")) {
      list.push({
        key: "staff",
        element: (
          <li key="nav-staff">
            {renderNavLink(
              "/staff",
              "Sub Administrators",
              <HiOutlineUserGroup className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }

    // 6. Email Templates
    if (checkAllow("email_templates,email-templates,emailtemplates")) {
      list.push({
        key: "email-templates",
        element: (
          <li key="nav-email-templates">
            {renderNavLink(
              "/email-templates",
              "Email Templates",
              <HiOutlineEnvelope className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }

    // ─── Legacy modules kept commented out as requested ───
    /*
    // 3. AI Chatbot
    if (checkAllow("chatbot")) {
      list.push({
        key: "chatbot",
        element: (
          <li key="nav-chatbot">
            {renderNavLink("/chatbot", "AI Chatbot", <FiMessageSquare className="w-4 h-4" />)}
          </li>
        ),
      });
    }

    // 4. Customers
    if (checkAllow("users,customers")) {
      list.push({
        key: "users",
        element: (
          <li key="nav-users">
            {renderNavLink("/users", "Customers", <FiUser className="w-4 h-4" />)}
          </li>
        ),
      });
    }

    // 5. Products
    if (checkAllow("products")) {
      list.push({
        key: "products",
        element: (
          <li key="nav-products">
            {renderNavLink("/products", "Products", <AiOutlineProduct className="w-4 h-4" />)}
          </li>
        ),
      });
    }

    // 6. Equipment
    if (checkAllow("equipment")) {
      list.push({
        key: "equipment",
        element: (
          <li key="nav-equipment">
            {renderNavLink("/equipment", "Equipment", <MdOutlineDisplaySettings className="w-4 h-4" />)}
          </li>
        ),
      });
    }

    // 7. Manufacturer
    if (checkAllow("manufacturer")) {
      list.push({
        key: "manufacturer",
        element: (
          <li key="nav-manufacturer">
            {renderNavLink("/manufacturer", "Manufacturer", <MdOutlineHomeWork className="w-4 h-4" />)}
          </li>
        ),
      });
    }

    // 8. Take Payment
    if (checkAllow("takePayment,payment")) {
      list.push({
        key: "take-payment",
        element: (
          <li key="nav-take-payment">
            {renderNavLink("/take/payment", "Take Payment", <MdOutlinePayment className="w-4 h-4" />)}
          </li>
        ),
      });
    }

    // 9. QuickBooks Online
    if (checkAllow("quickbooks,quickbook")) {
      list.push({
        key: "quickbooks-online",
        element: (
          <li key="nav-quickbooks-online">
            ...
          </li>
        ),
      });
    }

    // 10. QB Desktop
    if (checkAllow("qbDesktop,qb-desktop")) {
      list.push({
        key: "qb-desktop",
        element: (
          <li key="nav-qb-desktop">
            ...
          </li>
        ),
      });
    }

    // 11. Order Management
    if (checkAllow("orderManagement,orders")) {
      list.push({
        key: "order-management",
        element: (
          <li key="nav-order-management">
            {renderNavLink(
              "/order/management",
              "Order Management",
              <MdOutlineLocalOffer className="w-4 h-4" />
            )}
          </li>
        ),
      });
    }
    */

    return list;
  };

  return (
    <>
      <div
        className={`px-[0px] ${isOpen && styles.sm_sidebar}`}
        ref={navContainerRef}
      >
        <div className={`px-3 py-2.5 ${isOpen ? "text-center" : ""}`}>
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            {isOpen ? "•" : "Platform"}
          </p>
        </div>
        <ul className="space-y-1.5 !list-none scrollbar-hide h-[calc(100vh_-_140px)] overflow-auto px-2">
          {getSidebarItems().map((item) => item.element)}
        </ul>
      </div>
    </>
  );
};

export default Html;
